/* eslint-disable no-shadow */

import { CommandDefinition } from '../../../concepts/commands/CommandDefinition';
import { Attribute, Component, ComponentJson, ComponentTuple, Convention } from '../../../concepts/objects';
import { Line } from '../../../platform/geom/Line';
import { Matrix, MatrixTransformable } from '../../../platform/geom/Matrix';
import { Path } from '../../../platform/geom/Path';
import { HasXY, Point } from '../../../platform/geom/Point';
import { Polyline } from '../../../platform/geom/Polyline';
import { Rect } from '../../../platform/geom/Rect';
import { UIField } from './UIField';

// eslint-disable-next-line no-shadow
export enum RelativeTo {
    Parent = 1,
    Model = 2,
}

// eslint-disable-next-line no-shadow
export enum AnchorFlags {
    left = 1,
    right = 2,
    top = 4,
    bottom = 8,
}

export const IsCreating = new Attribute<boolean, Physical>('isCreating', false);

export type InteractionType = 'invoke' | 'drag' | 'select' | 'mutate';

export type Interaction = { readonly type: InteractionType, readonly hint?: string };
export const Interaction = (type: InteractionType, hint?: string): Interaction =>
    Object.freeze({ type, hint });

export interface PotentialInteraction {
    readonly type: InteractionType;
    readonly mpt: Point;
    readonly selected: boolean;
}

export const PotentialInteraction = (type: InteractionType, mpt: Point, selected: boolean = false): PotentialInteraction =>
    Object.freeze({ type, mpt, selected });

export interface PhysicalSnapshot {
    readonly x: number;
    readonly y: number;
    readonly rotation: number;
    readonly width: number;
    readonly height: number;
}

export const PhysicalSnapshot = Object.freeze({
    base: Object.freeze({ x: 0, y: 0, rotation: 0, width: 1, height: 1 }),
    fromManyAsIndex: (ps: Physical[]) => Object.fromEntries(ps.map(p => [p.id, PhysicalSnapshot.from(p)])),
    fromManyAsMap: (ps: Physical[]) => new Map(ps.map(p => [p, PhysicalSnapshot.from(p)])),
    from: (p: Physical) => {
        const { x, y } = p.position(RelativeTo.Model);
        const rotation = p.rotation(RelativeTo.Model);
        const size = p.size();
        return { x, y, rotation, width: size.w, height: size.h };
        // Obj.pick(p, 'x', 'y', 'rotation', 'width', 'height')
    },
});

export const TransformAttr = new Attribute<Matrix, Physical>('transformOverride', null);

export type TransformType = 'poly' | 'scale' | 'rotate' | 'translate' | 'tweak';

export class TransformScope implements Iterable<Physical> {

    public readonly items: readonly Physical[];
    public readonly type: TransformType;

    constructor(subjects: Physical | Physical[] | ReadonlySet<Physical>, type: TransformType) {
        if (subjects instanceof Component && subjects.is(Physical)) {
            subjects = [subjects as any];
        }
        this.items = Array.from(subjects as any);
        this.type = type;
    }

    [Symbol.iterator](): Iterator<Physical> {
        return this.items[Symbol.iterator]();
    }
}

export type TransformFigure = [Point, Point, Point, Point];

export const TransformFigure = {
    base: [new Point(0, 0), new Point(1, 0), new Point(1, 1), new Point(0, 1)] as TransformFigure,
    for(ref: PhysicalSnapshot) {
        return Matrix
            .translate(ref.x, ref.y)
            .rotate(ref.rotation)
            .scale(ref.width, ref.height)
            .apply(TransformFigure.base) as TransformFigure;
    },
};

export type Figure = readonly Point[];
export const Figure = {
    unit: new Rect(0, 0, 1, 1).points(),
    fromManyAsIndex<T extends Physical = Physical>(ts: T[]) {
        return Object.fromEntries(ts.map(t => [t.id, t.createFigure()]));
    },
    fromManyAsMap<T extends Physical = Physical>(ts: T[]) {
        return new Map(ts.map(t => [t, t.createFigure()]));
    },
};

export type TransformTweak = {
    readonly offset: number,
    readonly style?: 'node' | 'blob',
};

export type TransformActionVisibility = 'always' | 'proximity';
export type TransformActionOrientation = 'up' | 'right' | 'down' | 'left' | 'none' | Point;
export interface TransformAction {
    readonly key: string;
    readonly position: Point;
    readonly orientation: TransformActionOrientation;
    readonly command: CommandDefinition<[string, string]>;
    readonly visibility: TransformActionVisibility;
    readonly style: Record<string, any>;
}

export interface TransformHints {
    readonly scaleX?: boolean;
    readonly scaleY?: boolean;
    readonly scaleMode?: 'free' | 'fixed';
    readonly rotate?: boolean;
    readonly translate?: boolean;
    readonly tweaks?: TransformTweak[];
    readonly visible?: boolean;
}

const transformHints = (th: TransformHints) => Object.freeze(th) as TransformHints;

export const TransformHints = Object.assign(transformHints, {
    default: transformHints({
        scaleX: true,
        scaleY: true,
        rotate: true,
        translate: true,
        visible: true,
    }),
    off: transformHints({
        scaleX: false,
        scaleY: false,
        rotate: false,
        translate: false,
        visible: true,
    }),
});

export interface GuideLine {
    axis: 'x' | 'y';
    type: 'edge' | 'margin';
    position: number;
}

export const GuideLine = {
    fromRect: (rect: Rect): GuideLine[] => {
        return [
            GuideLine.x(rect.center().y),
            GuideLine.x(rect.top),
            GuideLine.x(rect.bottom),
            GuideLine.y(rect.center().x),
            GuideLine.y(rect.left),
            GuideLine.y(rect.right),
        ];
    },
    fromRectMargin: (rect: Rect, margin: number): GuideLine[] => {
        rect = rect.inflate(margin);
        return [
            GuideLine.mx(rect.top),
            GuideLine.mx(rect.bottom),
            GuideLine.my(rect.left),
            GuideLine.my(rect.right),
        ];
    },
    fromLine: ({ p1, p2 }: Line): GuideLine => {
        if (p1.x === p2.x) return GuideLine.y(p1.x);
        if (p1.y === p2.y) return GuideLine.x(p1.y);
        return null;
    },
    x: (position: number) => ({ axis: 'x' as const, position, type: 'edge' as const }),
    y: (position: number) => ({ axis: 'y' as const, position, type: 'edge' as const }),
    mx: (position: number) => ({ axis: 'x' as const, position, type: 'margin' as const }),
    my: (position: number) => ({ axis: 'y' as const, position, type: 'margin' as const }),
    plot: (gl: GuideLine, rect: Rect) => {
        if (gl.axis === 'x') {
            return [new Point(rect.left, gl.position), new Point(rect.right, gl.position)];
        }
        return [new Point(gl.position, rect.top), new Point(gl.position, rect.bottom)];
    },
};


export const Physical = new Convention<Physical>('Physical');

/**
 * Represents a component that is visible on the board render and can be manipulated with the tool system.
 */
export interface Physical extends Component {

    /**
     * Whether or not the component is a boundary.  Boundaries stop interaction with their children.  Think of it like putting a lid on top
     * that you can see through.  You can see the contents but cannot access them.
     */
    readonly boundary: boolean;

    /**
     * The parent of the component cast as a Physical.  Returns null if the parent is not a Physical (or is null).
     */
    readonly parentPhysical: Physical | null;

    readonly uiFields: readonly UIField[];

    name: string;

    anchor: number;

    /**
     * Whether or not the component is locked.
     */
    locked: boolean;

    bounded(): boolean;

    createFigure(): Figure;

    setFromFigure(figure: Figure): void;

    /**
     * Gets the translate and rotate matrix that places the component where it is, relative to the specified reference.  This matrix will
     * not include the sizing as scale, scale will always be 1.
     *
     * @param to
     */
    transform(to?: RelativeTo | Physical): Matrix;

    /**
     * Gets the position of the component relative to the specified reference.
     *
     * @param to
     */
    position(to?: RelativeTo | Physical): Point;

    /**
     * Gets the center position of the component relative to the specified reference.
     *
     * @param to
     */
    center(to?: RelativeTo | Physical): Point;

    /**
     * Gets the rotation of the component relative to the specified reference.
     *
     * @param to
     */
    rotation(to?: RelativeTo | Physical): number;

    /**
     * Gets the size of the component.
     */
    size(): Point;

    /**
     * Gets the bounding box of the area that is covered by any part of the component.  This is distinct from bounds in that while bounds
     * returns the bounding box of the main physical points, it does not include any overlay or outlying elements.  Some components may want
     * to render visuals that flow or escape out of the bounds, that are clickable, but do not effect the packing or layout behavior of the
     * component.  An example of this is the status indicator on Stickys.  They are clickable, but should not cause a Sticky to fall out of
     * its container if placed at 0,0.  Essentially this property exists to power the spatial index.
     * @param to
     */
    area(to?: RelativeTo | Physical): Rect;

    /**
     * Gets the bounding box of the component, based on the position, size and rotation.  This is potentially distinct from area.
     *
     * @param to
     */
    bounds(to?: RelativeTo | Physical): Rect;

    /**
     * Gets a Polyline that contains the wireframe of the component.
     *
     * @param to
     */
    outline(to?: RelativeTo | Physical): Polyline;

    /**
     * Gets a Path that contains the outline of the component.
     *
     * @param to
     */
    path(to?: RelativeTo | Physical): Path;

    test(mpt: Point): boolean;

    testInteraction(pi: PotentialInteraction): [boolean, string];

    queryGuides(): GuideLine[];

    queryTransformActions(singular: boolean): TransformAction[];

    queryTransformHints(singular: boolean): TransformHints;

    applyTransformation(scope: TransformScope, from: Figure, to: Figure): this;

    moveBy(distX: number, distY: number): this;
    moveBy(dist: Partial<HasXY>): this;

    moveTo(toX: number, toY: number): this;
    moveTo(to: Partial<HasXY>): this;

    rotateBy(radians: number): this;
    rotateTo(radians: number): this;

    toRelative(mpt: Point): Point;
    toRelative(mpts: readonly Point[]): Point[];
    toRelative<T>(mg: MatrixTransformable<T>): T;

    toEmptyTuple(to?: RelativeTo | Physical): ComponentTuple;
    toShallowTuple(to?: RelativeTo | Physical): ComponentTuple;
    toTuple(to?: RelativeTo | Physical): ComponentTuple;

    toJson(to?: RelativeTo | Physical): ComponentJson;
}
